import React, { useState, useEffect } from 'react';
import { apiService, Vehicle } from '../services/api';
import {
  saveVehicleDraft,
  loadVehicleDraft,
  clearVehicleDraft,
  markVehicleFreshStart,
  clearVehicleFreshFlag,
  isVehicleFreshStart,
} from '../utils/vehicleFormPersistence';

interface AddVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddVehicle: (vehicle: Vehicle) => void;
}

const AddVehicleModal: React.FC<AddVehicleModalProps> = ({ isOpen, onClose, onAddVehicle }) => {
  const [formData, setFormData] = useState({
    vehicleNumber: '',
    vehicleType: '',
    vehicleInsurance: '',
    vehicleInsuranceNumber: '',
    vehicleInsuranceExpiry: '',
    vehiclePermitNumber: '',
    vehiclePermitExpiry: '',
    vehiclePollutionNumber: '',
    vehiclePollutionExpiry: '',
    vehicleRCNumber: '',
    vehicleRcExpiry: '',
    capacity: '',
    // File uploads
    vehicleImage: null as File | null,
    vehicleInsuranceImage: null as File | null,
    vehiclePermitImage: null as File | null,
    vehiclePollutionImage: null as File | null,
    vehicleRcImage: null as File | null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vehicleTypes, setVehicleTypes] = useState<string[]>([]);
  const [isLoadingVehicleTypes, setIsLoadingVehicleTypes] = useState(false);
  const [showRestore, setShowRestore] = useState(false);
  const [draftData, setDraftData] = useState<any>(null);
  const [validationErrors, setValidationErrors] = useState<{
    vehicleNumber?: string;
    vehicleType?: string;
    vehicleInsurance?: string;
    vehicleInsuranceNumber?: string;
    vehicleInsuranceExpiry?: string;
    vehiclePermitNumber?: string;
    vehiclePermitExpiry?: string;
    vehiclePollutionNumber?: string;
    vehiclePollutionExpiry?: string;
    vehicleRCNumber?: string;
    vehicleRcExpiry?: string;
  }>({});

  // Debug: Log vehicleTypes state changes
  useEffect(() => {
    console.log('AddVehicleModal: Current vehicleTypes state:', vehicleTypes);
  }, [vehicleTypes]);

  // Fetch vehicle types when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchVehicleTypes();
    }
  }, [isOpen]);

  // Load draft when modal opens
  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        vehicleNumber: '',
        vehicleType: '',
        vehicleInsurance: '',
        vehicleInsuranceNumber: '',
        vehicleInsuranceExpiry: '',
        vehiclePermitNumber: '',
        vehiclePermitExpiry: '',
        vehiclePollutionNumber: '',
        vehiclePollutionExpiry: '',
        vehicleRCNumber: '',
        vehicleRcExpiry: '',
        capacity: '',
        vehicleImage: null,
        vehicleInsuranceImage: null,
        vehiclePermitImage: null,
        vehiclePollutionImage: null,
        vehicleRcImage: null
      });
      setValidationErrors({});
      setDraftData(null);
      setShowRestore(false);
      return;
    }
    
    // Reset form first
    setFormData({
      vehicleNumber: '',
      vehicleType: '',
      vehicleInsurance: '',
      vehicleInsuranceNumber: '',
      vehicleInsuranceExpiry: '',
      vehiclePermitNumber: '',
      vehiclePermitExpiry: '',
      vehiclePollutionNumber: '',
      vehiclePollutionExpiry: '',
      vehicleRCNumber: '',
      vehicleRcExpiry: '',
      capacity: '',
      vehicleImage: null,
      vehicleInsuranceImage: null,
      vehiclePermitImage: null,
      vehiclePollutionImage: null,
      vehicleRcImage: null
    });
    setValidationErrors({});
    
    if (isVehicleFreshStart()) return;

    const draft = loadVehicleDraft();
    if (draft) {
      setDraftData(draft);
      setShowRestore(true);
    }
  }, [isOpen]);

  // Save draft on form changes
  useEffect(() => {
    if (!isOpen || showRestore) return;
    
    // Debounce save to avoid too many writes
    const timeoutId = setTimeout(() => {
      saveVehicleDraft(formData);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData, isOpen, showRestore]);

  const fetchVehicleTypes = async () => {
    setIsLoadingVehicleTypes(true);
    try {
      const response = await apiService.getVehicleTypes({
        limit: 100, // Get all vehicle types
        sortBy: 'name',
        sortOrder: 'asc'
      });
      
      if (response.success && response.data) {
        // Handle nested response structure
        const vehicleTypesData = (response.data as any).data || response.data;
        setVehicleTypes(vehicleTypesData);
      } else {
        console.error('Failed to fetch vehicle types:', response.error);
        setVehicleTypes([]);
        setError('Failed to load vehicle types. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching vehicle types:', error);
      setVehicleTypes([]);
      setError('Error loading vehicle types. Please check your connection and try again.');
    } finally {
      setIsLoadingVehicleTypes(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validate required fields
    const errors = {
      vehicleNumber: validateIndianVehicleRegistration(formData.vehicleNumber),
      vehicleType: validateRequiredText(formData.vehicleType, 'Vehicle type is required'),
      vehicleInsurance: validateRequiredText(formData.vehicleInsurance, 'Insurance company is required'),
      vehicleInsuranceNumber: validateDocumentNumber(formData.vehicleInsuranceNumber, 'Insurance number', 6, 30),
      vehicleInsuranceExpiry: validateRequiredText(formData.vehicleInsuranceExpiry, 'Insurance expiry date is required'),
      vehiclePermitNumber: validateDocumentNumber(formData.vehiclePermitNumber, 'Permit number', 3, 20),
      vehiclePermitExpiry: validateRequiredText(formData.vehiclePermitExpiry, 'Permit expiry date is required'),
      vehiclePollutionNumber: validateDocumentNumber(formData.vehiclePollutionNumber, 'Pollution certificate number', 6, 20),
      vehiclePollutionExpiry: validateRequiredText(formData.vehiclePollutionExpiry, 'Pollution certificate expiry is required'),
      vehicleRCNumber: validateDocumentNumber(formData.vehicleRCNumber, 'RC number', 5, 20),
      vehicleRcExpiry: validateRequiredText(formData.vehicleRcExpiry, 'RC expiry date is required'),
    } as typeof validationErrors;
    setValidationErrors(errors);
    const hasErrors = Object.values(errors).some(Boolean);
    if (hasErrors) {
      setIsLoading(false);
      return;
    }

    try {
      let isElixiaPresent = false;
      const elixiaCheckResponse = await apiService.checkVehicleInElixia(formData.vehicleNumber);
      if (elixiaCheckResponse.success && elixiaCheckResponse.data) {
        isElixiaPresent = elixiaCheckResponse.data.isElixiaPresent;
      }

      const vehicleData = {
        vehicleNumber: formData.vehicleNumber,
        vehicleType: formData.vehicleType,
        vehicleInsurance: formData.vehicleInsurance,
        vehicleInsuranceNumber: formData.vehicleInsuranceNumber,
        vehicleInsuranceExpiry: formData.vehicleInsuranceExpiry,
        vehiclePermit: 'N/A',
        vehiclePermitNumber: formData.vehiclePermitNumber,
        vehiclePermitExpiry: formData.vehiclePermitExpiry,
        vehiclePollution: 'N/A',
        vehiclePollutionNumber: formData.vehiclePollutionNumber,
        vehiclePollutionExpiry: formData.vehiclePollutionExpiry,
        vehicleRc: 'N/A',
        vehicleRCNumber: formData.vehicleRCNumber,
        vehicleRcExpiry: formData.vehicleRcExpiry,
        capacity: formData.capacity,
        status: 'Active' as const,
        isElixia: isElixiaPresent,
        // File uploads
        vehicleImage: formData.vehicleImage || undefined,
        vehicleInsuranceImage: formData.vehicleInsuranceImage || undefined,
        vehiclePermitImage: formData.vehiclePermitImage || undefined,
        vehiclePollutionImage: formData.vehiclePollutionImage || undefined,
        vehicleRcImage: formData.vehicleRcImage || undefined
      };


      const response = await apiService.addVehicle(vehicleData);
      
      if (response.success && response.data) {
        onAddVehicle(response.data);
        clearVehicleDraft();
        clearVehicleFreshFlag();
        // Reset form data
        setFormData({
          vehicleNumber: '',
          vehicleType: '',
          vehicleInsurance: '',
          vehicleInsuranceNumber: '',
          vehicleInsuranceExpiry: '',
          vehiclePermitNumber: '',
          vehiclePermitExpiry: '',
          vehiclePollutionNumber: '',
          vehiclePollutionExpiry: '',
          vehicleRCNumber: '',
          vehicleRcExpiry: '',
          capacity: '',
          vehicleImage: null,
          vehicleInsuranceImage: null,
          vehiclePermitImage: null,
          vehiclePollutionImage: null,
          vehicleRcImage: null
        });
        setDraftData(null);
        setShowRestore(false);
        onClose();
      } else {
        setError(response.error || 'Failed to add vehicle');
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (fieldName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData({ ...formData, [fieldName]: file });
  };

  // Simple required text validator
  const validateRequiredText = (value: string, message: string): string | undefined => {
    if (!value || !value.toString().trim()) return message;
    return undefined;
  };

  // Indian formats validation helpers
  const normalizeForValidation = (value: string): string => (value || '').toUpperCase().trim();

  // Vehicle Registration Number (e.g., MH01AB1234, KA01AA1234, DL1CAB1234, 21BH1234AA)
  const validateIndianVehicleRegistration = (value: string): string | undefined => {
    const normalized = normalizeForValidation(value).replace(/[\s-]/g, '');
    if (!normalized) return 'Vehicle number is required';
    const standardReg = /^[A-Z]{2}\d{2}[A-Z]{1,2}\d{4}$/; // e.g., MH01AB1234 or DL01C1234
    const bhSeries = /^\d{2}BH\d{4}[A-Z]{2}$/; // e.g., 21BH1234AA
    if (standardReg.test(normalized) || bhSeries.test(normalized)) return undefined;
    return 'Enter a valid Indian vehicle number (e.g., MH01AB1234 or 21BH1234AA)';
  };

  // Generic document number: allow A-Z, 0-9, '-', '/' with length bounds
  const validateDocumentNumber = (
    value: string,
    label: string,
    minLen: number,
    maxLen: number,
    pattern: RegExp = /^[A-Z0-9\-/]+$/
  ): string | undefined => {
    const normalized = normalizeForValidation(value);
    if (!normalized) return `${label} is required`;
    if (normalized.length < minLen || normalized.length > maxLen) {
      return `${label} should be ${minLen}-${maxLen} characters`;
    }
    if (!pattern.test(normalized)) {
      return `${label} can include A-Z, 0-9, '-' and '/' only`;
    }
    return undefined;
  };

  const handleRestore = () => {
    if (draftData) {
      setFormData(prev => ({
        ...prev,
        ...draftData,
        // Files cannot be restored
        vehicleImage: null,
        vehicleInsuranceImage: null,
        vehiclePermitImage: null,
        vehiclePollutionImage: null,
        vehicleRcImage: null,
      }));
    }
    clearVehicleFreshFlag();
    setShowRestore(false);
    setDraftData(null);
  };

  const handleStartFresh = () => {
    clearVehicleDraft();
    markVehicleFreshStart();
    setShowRestore(false);
    setDraftData(null);
    // Reset form to empty values
    setFormData({
      vehicleNumber: '',
      vehicleType: '',
      vehicleInsurance: '',
      vehicleInsuranceNumber: '',
      vehicleInsuranceExpiry: '',
      vehiclePermitNumber: '',
      vehiclePermitExpiry: '',
      vehiclePollutionNumber: '',
      vehiclePollutionExpiry: '',
      vehicleRCNumber: '',
      vehicleRcExpiry: '',
      capacity: '',
      vehicleImage: null,
      vehicleInsuranceImage: null,
      vehiclePermitImage: null,
      vehiclePollutionImage: null,
      vehicleRcImage: null
    });
    setValidationErrors({});
  };

  const handleClose = () => {
    clearVehicleFreshFlag();
    setShowRestore(false);
    setDraftData(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <React.Fragment>
      {showRestore && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <h2 className="text-lg font-semibold mb-2">Restore Form?</h2>
            <p className="text-sm text-gray-600 mb-4">
              Unsaved form data was found. Do you want to restore it?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleStartFresh}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Start Fresh
              </button>
              <button
                onClick={handleRestore}
                className="px-4 py-2 bg-jd-green text-white rounded-md hover:bg-green-600 transition-colors"
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Add Vehicle</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={() => {
              if (showRestore) return;
              handleClose();
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={showRestore}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Basic Vehicle Information */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Number *</label>
                <input
                  type="text"
                  placeholder="e.g., MH01AB1234"
                  value={formData.vehicleNumber}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData({ ...formData, vehicleNumber: v });
                    setValidationErrors(prev => ({ ...prev, vehicleNumber: validateIndianVehicleRegistration(v) }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                    validationErrors.vehicleNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {validationErrors.vehicleNumber && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.vehicleNumber}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Type *</label>
                <select
                  value={formData.vehicleType}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData({ ...formData, vehicleType: v });
                    setValidationErrors(prev => ({ ...prev, vehicleType: validateRequiredText(v, 'Vehicle type is required') }));
                  }}
                  disabled={isLoadingVehicleTypes || vehicleTypes.length === 0}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    validationErrors.vehicleType ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">
                    {isLoadingVehicleTypes 
                      ? 'Loading vehicle types...' 
                      : vehicleTypes.length === 0 
                        ? 'No vehicle types available' 
                        : 'Select Vehicle Type'
                    }
                  </option>
                  {Array.isArray(vehicleTypes) && vehicleTypes.length > 0 && vehicleTypes
                    .map((type, index) => {
                    
                      return (
                        <option key={index} value={type}>
                          {type}
                        </option>
                      );
                    })}
                </select>
                {isLoadingVehicleTypes && (
                  <div className="mt-1 text-xs text-gray-500">
                    Loading vehicle types from server...
                  </div>
                )}
                {!isLoadingVehicleTypes && vehicleTypes.length === 0 && (
                  <div className="mt-1 text-xs text-red-500">
                    No vehicle types available. Please contact administrator.
                  </div>
                )}
                {validationErrors.vehicleType && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.vehicleType}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Capacity</label>
                <input
                  type="text"
                  placeholder="e.g., 1000 kg"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent"
                />
              </div>
              
            </div>
          </div>

          {/* Insurance Information */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Insurance Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Company *</label>
                <input
                  type="text"
                  placeholder="e.g., New India Assurance Company"
                  value={formData.vehicleInsurance}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData({ ...formData, vehicleInsurance: v });
                    setValidationErrors(prev => ({ ...prev, vehicleInsurance: validateRequiredText(v, 'Insurance company is required') }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                    validationErrors.vehicleInsurance ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {validationErrors.vehicleInsurance && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.vehicleInsurance}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Number *</label>
                <input
                  type="text"
                  placeholder="e.g., NIA123456789"
                  value={formData.vehicleInsuranceNumber}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData({ ...formData, vehicleInsuranceNumber: v });
                    setValidationErrors(prev => ({ ...prev, vehicleInsuranceNumber: validateDocumentNumber(v, 'Insurance number', 6, 30) }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                    validationErrors.vehicleInsuranceNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {validationErrors.vehicleInsuranceNumber && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.vehicleInsuranceNumber}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Expiry *</label>
                <input
                  type="date"
                  value={formData.vehicleInsuranceExpiry}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData({ ...formData, vehicleInsuranceExpiry: v });
                    setValidationErrors(prev => ({ ...prev, vehicleInsuranceExpiry: validateRequiredText(v, 'Insurance expiry date is required') }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                    validationErrors.vehicleInsuranceExpiry ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {validationErrors.vehicleInsuranceExpiry && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.vehicleInsuranceExpiry}</p>
                )}
              </div>
            </div>
          </div>

          {/* Permit Information */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Permit Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permit Number *</label>
                <input
                  type="text"
                  placeholder="e.g., PER123456789"
                  value={formData.vehiclePermitNumber}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData({ ...formData, vehiclePermitNumber: v });
                    setValidationErrors(prev => ({ ...prev, vehiclePermitNumber: validateDocumentNumber(v, 'Permit number', 3, 20) }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                    validationErrors.vehiclePermitNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {validationErrors.vehiclePermitNumber && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.vehiclePermitNumber}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permit Expiry *</label>
                <input
                  type="date"
                  value={formData.vehiclePermitExpiry}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData({ ...formData, vehiclePermitExpiry: v });
                    setValidationErrors(prev => ({ ...prev, vehiclePermitExpiry: validateRequiredText(v, 'Permit expiry date is required') }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                    validationErrors.vehiclePermitExpiry ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {validationErrors.vehiclePermitExpiry && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.vehiclePermitExpiry}</p>
                )}
              </div>
            </div>
          </div>

          {/* Pollution Certificate Information */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Pollution Certificate</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pollution Certificate Number *</label>
                <input
                  type="text"
                  placeholder="e.g., PUC123456789"
                  value={formData.vehiclePollutionNumber}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData({ ...formData, vehiclePollutionNumber: v });
                    setValidationErrors(prev => ({ ...prev, vehiclePollutionNumber: validateDocumentNumber(v, 'Pollution certificate number', 6, 20) }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                    validationErrors.vehiclePollutionNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {validationErrors.vehiclePollutionNumber && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.vehiclePollutionNumber}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pollution Certificate Expiry *</label>
                <input
                  type="date"
                  value={formData.vehiclePollutionExpiry}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData({ ...formData, vehiclePollutionExpiry: v });
                    setValidationErrors(prev => ({ ...prev, vehiclePollutionExpiry: validateRequiredText(v, 'Pollution certificate expiry is required') }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                    validationErrors.vehiclePollutionExpiry ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {validationErrors.vehiclePollutionExpiry && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.vehiclePollutionExpiry}</p>
                )}
              </div>
            </div>
          </div>

          {/* RC Information */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Registration Certificate (RC)</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">RC Number *</label>
                <input
                  type="text"
                  placeholder="e.g., RC123456789"
                  value={formData.vehicleRCNumber}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData({ ...formData, vehicleRCNumber: v });
                    setValidationErrors(prev => ({ ...prev, vehicleRCNumber: validateDocumentNumber(v, 'RC number', 5, 20) }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                    validationErrors.vehicleRCNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {validationErrors.vehicleRCNumber && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.vehicleRCNumber}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">RC Expiry *</label>
                <input
                  type="date"
                  value={formData.vehicleRcExpiry}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData({ ...formData, vehicleRcExpiry: v });
                    setValidationErrors(prev => ({ ...prev, vehicleRcExpiry: validateRequiredText(v, 'RC expiry date is required') }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                    validationErrors.vehicleRcExpiry ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {validationErrors.vehicleRcExpiry && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.vehicleRcExpiry}</p>
                )}
              </div>
            </div>
          </div>

          {/* File Uploads */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Document Uploads (Optional)</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Vehicle Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Image</label>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                    onClick={() => document.getElementById('vehicle-image-upload')?.click()}
                  >
                    Upload
                  </button>
                  <div className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                    <span className="text-gray-500 text-sm">
                      {formData.vehicleImage ? (formData.vehicleImage as File).name : 'No file selected'}
                    </span>
                  </div>
                </div>
                <input
                  id="vehicle-image-upload"
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={handleFileChange('vehicleImage')}
                  className="hidden"
                />
              </div>

              {/* Insurance Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Document</label>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                    onClick={() => document.getElementById('insurance-image-upload')?.click()}
                  >
                    Upload
                  </button>
                  <div className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                    <span className="text-gray-500 text-sm">
                      {formData.vehicleInsuranceImage ? (formData.vehicleInsuranceImage as File).name : 'No file selected'}
                    </span>
                  </div>
                </div>
                <input
                  id="insurance-image-upload"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange('vehicleInsuranceImage')}
                  className="hidden"
                />
              </div>

              {/* Permit Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permit Document</label>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                    onClick={() => document.getElementById('permit-image-upload')?.click()}
                  >
                    Upload
                  </button>
                  <div className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                    <span className="text-gray-500 text-sm">
                      {formData.vehiclePermitImage ? (formData.vehiclePermitImage as File).name : 'No file selected'}
                    </span>
                  </div>
                </div>
                <input
                  id="permit-image-upload"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange('vehiclePermitImage')}
                  className="hidden"
                />
              </div>

              {/* Pollution Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pollution Certificate</label>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                    onClick={() => document.getElementById('pollution-image-upload')?.click()}
                  >
                    Upload
                  </button>
                  <div className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                    <span className="text-gray-500 text-sm">
                      {formData.vehiclePollutionImage ? (formData.vehiclePollutionImage as File).name : 'No file selected'}
                    </span>
                  </div>
                </div>
                <input
                  id="pollution-image-upload"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange('vehiclePollutionImage')}
                  className="hidden"
                />
              </div>

              {/* RC Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">RC Document</label>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                    onClick={() => document.getElementById('rc-image-upload')?.click()}
                  >
                    Upload
                  </button>
                  <div className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                    <span className="text-gray-500 text-sm">
                      {formData.vehicleRcImage ? (formData.vehicleRcImage as File).name : 'No file selected'}
                    </span>
                  </div>
                </div>
                <input
                  id="rc-image-upload"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange('vehicleRcImage')}
                  className="hidden"
                />
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={showRestore}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors order-2 sm:order-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || showRestore}
              className="px-4 py-2 bg-jd-green text-white rounded-lg hover:bg-green-600 transition-colors order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Adding...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
      </div>
    </React.Fragment>
  );
};

export default AddVehicleModal;
